import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PEOPLE_FILTER_MODELS } from 'src/app/shared/components/odata-filter/models/system-filter-models';
import { ApiVersions } from 'src/app/shared/services/api-version';
import { BaseODataService, ODataQueryParams } from 'src/app/shared/services/base-odata.service';
import { Image } from '../models/image';
import { People } from '../models/people.model';
import { PeopleFilterFields } from '../models/peopleFilterFields';
import { PeopleFilterResult } from '../models/peopleFilterResult.model';
import { Person } from '../models/person';
import { MergeProfileResult } from './merge-profile-result';
import { MergeProfileModel } from './merge-profile.model';
import { MergeProfileErrorModalComponent } from './merge=profile-error-modal/merge-profile-error-modal.component';

@Injectable({
  providedIn: 'root'
})
export class MergeProfileService extends BaseODataService {
  private _mainProfile: MergeProfileModel | null = null;
  private _searchedProfile: MergeProfileModel | null = null;
  private _profileSwapped: boolean = false;
  public readonly mainProfile = new BehaviorSubject<MergeProfileModel | null>(null);
  public readonly searchedProfile = new BehaviorSubject<MergeProfileModel | null>(null);
  public readonly mergeResult = new BehaviorSubject<MergeProfileResult | null>(null);
  constructor() {
    super();
    this._baseUrl = 'people';
    this._searchTextModel = PEOPLE_FILTER_MODELS.SearchFilterModel;
    this._filterResult = new PeopleFilterResult();
  }

  get profileSwapped(): boolean {
    return this._profileSwapped;
  }

  get primaryProfile(): MergeProfileModel {
    return this._profileSwapped ? this._mainProfile : this._searchedProfile;
  }

  get secondaryProfile(): MergeProfileModel {
    return this._profileSwapped ? this._searchedProfile : this._mainProfile;
  }
  setMainProfile(value: MergeProfileModel | null) {
    this._mainProfile = value;
    this.mainProfile.next(this._mainProfile);
  }
  setSearchedProfile(value: MergeProfileModel | null) {
    this._searchedProfile = value;
    this.searchedProfile.next(this._searchedProfile);
  }
  swapProfiles() {
    this._profileSwapped = !this._profileSwapped;
  }

  isMergeable() {
    if (!this._mainProfile || !this._searchedProfile) {
      return false;
    }

    if (this._mainProfile.id == this._searchedProfile.id) {
      return false;
    }

    return true;
  }
  search(value: string) {
    if (this._mainProfile) {
      if (!value || value.length < 2) return of([]);
      this.searchText = value;
      let params: ODataQueryParams = {
        select: [
          PeopleFilterFields.id,
          PeopleFilterFields.fullName,
          PeopleFilterFields.email,
          PeopleFilterFields.tags,
          PeopleFilterFields.updatedOn,
          PeopleFilterFields.status,
          PeopleFilterFields.imageId
        ],
        filter: this.buildFilter([]),
        count: false,
        expand: [PeopleFilterFields.tags],
        orderBy: [PeopleFilterFields.fullName]
      };
      return this.getODataResult(this.filterUrl, params, ApiVersions.v1_0).pipe(
        map((response) => {
          if (response && response.value) {
            return response.value
              .map((v: People) => {
                if (v.imageId) {
                  v.image = new Image(v.imageId);
                }
                return new MergeProfileModel(v);
              })
              .filter((p: People) => p.id != this._mainProfile?.id);
          } else {
            return [];
          }
        })
      );
    } else {
      throw new Error('The main profile is not set');
    }
  }

  merge(mergeEmails: boolean = true) {
    let path = `users/${this.primaryProfile.id}/merge/${this.secondaryProfile.id}?mergeEmails=${mergeEmails}`;
    return this.put(path, null, null, ApiVersions.v1_0, true).pipe(
      map((result) => {
        if (result) {
          this.mergeResult.next({
            mergedProfile: new Person(result),
            deletedProfile: new Person(this.secondaryProfile)
          });
          return true;
        }
        return false;
      }),
      catchError((err: HttpErrorResponse) => {
        let error = err.error;
        if (error && error.errorMessages && Array.isArray(error.errorMessages)) {
          this.dialog.open(MergeProfileErrorModalComponent, {
            data: error.errorMessages,
            panelClass: 'plan-dialog'
          });
          return of(null);
        } else {
          this.handleError(err, true, false);
          return of(null);
        }
      })
    );
  }
}
